import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Select,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AddProject: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const { user } = useAuth();

  // Determine if in edit mode
  const isEditMode = Boolean(id);

  // If edit mode, fetch project from API
  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    clientId: '',
    client: '',
    startDate: '',
    endDate: '',
    budget: '',
    currency: '',
    status: 'planning',
    priority: 'medium',
    associates: [],
    notes: '',
    managerName: '',
    managerEmail: '',
    managerContact: '',
  });
  const [associates, setAssociates] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);
  React.useEffect(() => {
    // Fetch clients for dropdown
    import('../services/api').then(m => m.clientsAPI.getAll()).then(res => {
      setClients(res.data);
      // Fetch associates for multi-select
      import('../services/api').then(m => m.associateAPI.getAllAssociates()).then(resA => {
        setAssociates(resA.data);
        // Fetch managers for dropdown
        import('../services/api').then(m => m.userAPI.getAllUsers()).then(resU => {
          setManagers(resU.data.filter((u: any) => u.role === 'manager'));
          if (isEditMode && id) {
            import('../services/api').then(m => m.projectsAPI.getById(id)).then(res2 => {
              // If editing, ensure clientId and client fields are set
              let clientId = '';
              let clientName = '';
              if (res2.data.client) {
                // Find client by name (company)
                const found = res2.data.clientId
                  ? res2.data.clientId
                  : (res.data.find((c: any) => c.company === (typeof res2.data.client === 'string' ? res2.data.client : res2.data.client.company))?._id || '');
                clientId = found;
                clientName = typeof res2.data.client === 'string' ? res2.data.client : res2.data.client.company;
              }
              setFormData({ ...res2.data, clientId, client: clientName });
            });
          }
        });
      });
    });
      // Removed usage of undefined 'project' variable to fix compile error.
  }, [isEditMode, id]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

  // Ensure clientId, currency, and managerContact are set
  if (!formData.clientId || !formData.currency || !formData.managerContact) {
      toast({
        title: 'Missing Fields',
        description: 'Please select both client and currency.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Attach manager's userId to project data
      const projectData = { ...formData, userId: user?._id };
      if (isEditMode && id) {
        await import('../services/api').then(m => m.projectsAPI.update(id, projectData));
        toast({
          title: 'Project Updated Successfully',
          description: `${formData.name}'s details have been updated.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        await import('../services/api').then(m => m.projectsAPI.create(projectData));
        toast({
          title: 'Project Created Successfully',
          description: `${formData.name} has been added to your project list.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      navigate('/projects');
    } catch (error: any) {
      toast({
        title: isEditMode ? 'Error Updating Project' : 'Error Creating Project',
        description: error.message || 'There was an error. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  return (
    <Box>
      {/* Page Header */}
      <Box mb={8}>
        <HStack spacing={4} mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={handleCancel}
            color="gray.600"
          >
            Back to Projects
          </Button>
        </HStack>
        <Heading size="lg" color="gray.800" mb={2}>
          {isEditMode ? 'Edit Project' : 'Create New Project'}
        </Heading>
        <Text color="gray.600">
          {isEditMode ? 'Update project details and save changes.' : 'Set up a new project and assign team members'}
        </Text>
      </Box>

      {/* Add Project Form */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={8}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Project Details
                </Heading>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Project Name</FormLabel>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter project name"
                        className="form-input"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Client</FormLabel>
                      <Select
                        value={formData.clientId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedClient = clients.find(c => c._id === selectedId);
                          setFormData({
                            ...formData,
                            clientId: selectedId,
                            client: selectedClient ? selectedClient.company : '',
                          });
                        }}
                        placeholder="Select client"
                        className="form-input"
                      >
                        {clients.map((client) => (
                          <option key={client._id} value={client._id}>
                            {client.company}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        placeholder="Select currency"
                        className="form-input"
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </Select>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Timeline & Budget */}
              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Timeline & Budget
                </Heading>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Start Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="form-input"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>End Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="form-input"
                      />
                    </FormControl>
                  </HStack>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Budget</FormLabel>
                      <NumberInput
                        value={formData.budget}
                        onChange={(value) => setFormData({ ...formData, budget: value })}
                        min={0}
                        precision={2}
                      >
                        <NumberInputField placeholder="Enter budget amount" className="form-input" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Priority</FormLabel>
                      <Select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'low' | 'medium' | 'high' })}
                        className="form-input"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Total Billable Hours (per day)</FormLabel>
                      <NumberInput
                        value={formData.totalBillableHoursPerDay || ''}
                        onChange={value => setFormData({ ...formData, totalBillableHoursPerDay: value })}
                        min={0}
                        precision={2}
                      >
                        <NumberInputField placeholder="Enter billable hours per day" className="form-input" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Project Manager Section */}
              <Box>
                <Heading size="md" color="gray.700" mb={4}>Project Manager</Heading>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Manager</FormLabel>
                      <Select
                        placeholder="Select manager"
                        value={formData.managerId || ''}
                        onChange={e => {
                          const selectedManager = managers.find((m: any) => m._id === e.target.value);
                          setFormData({
                            ...formData,
                            managerId: selectedManager?._id || '',
                            managerName: selectedManager?.name || '',
                            managerEmail: selectedManager?.email || '',
                            userId: selectedManager?._id || '',
                          });
                        }}
                        className="form-input"
                      >
                        {managers.map((manager: any) => (
                          <option key={manager._id} value={manager._id}>
                            {manager.name} ({manager.email})
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Manager Contact Number</FormLabel>
                      <Input
                        type="tel"
                        value={formData.managerContact}
                        onChange={e => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          const limited = digitsOnly.slice(0, 10);
                          setFormData({ ...formData, managerContact: limited });
                        }}
                        placeholder="Enter 10-digit manager contact number"
                        className="form-input"
                        maxLength={10}
                        pattern="\d{10}"
                      />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Status & Team */}
              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Status & Team
                </Heading>
                <VStack spacing={4}>
                  {/* Status field in its own row */}
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Status</FormLabel>
                      <Select
                        placeholder="Select status"
                        value={formData.status}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            status: e.target.value as
                              | 'planning'
                              | 'active'
                              | 'on-hold'
                              | 'completed'
                              | 'cancelled',
                          })
                        }
                        className="form-input"
                      >
                        <option value="planning">Planning</option>
                        <option value="active">Active</option>
                        <option value="on-hold">On Hold</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </Select>
                    </FormControl>
                  </HStack>
                  {/* Associates field in its own row */}
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Associates</FormLabel>
                      <Wrap spacing={4}>
                        {formData.associates && formData.associates.length > 0 ? (
                          formData.associates.map((selectedId: string, idx: number) => (
                            <WrapItem key={idx}>
                              <HStack>
                                <Select
                                  placeholder="Select associate"
                                  value={selectedId}
                                  onChange={(e) => {
                                    const updated = [...formData.associates];
                                    updated[idx] = e.target.value;
                                    setFormData({ ...formData, associates: updated });
                                  }}
                                  w="220px"
                                >
                                  {associates
                                    .filter(a =>
                                      // Show associate if not already selected, or is the current value
                                      !formData.associates.includes(a._id) || a._id === selectedId
                                    )
                                    .map(a => (
                                      <option key={a._id} value={a._id}>
                                        {a.name} ({a.department})({a.position})
                                      </option>
                                    ))}
                                </Select>
                                {formData.associates.length > 1 && (
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    onClick={() => {
                                      const updated = formData.associates.filter((_: any, i: number) => i !== idx);
                                      setFormData({ ...formData, associates: updated });
                                    }}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </HStack>
                            </WrapItem>
                          ))
                        ) : (
                          <WrapItem>
                            <Select
                              placeholder="Select associate"
                              value=""
                              onChange={e => setFormData({ ...formData, associates: [e.target.value] })}
                              w="220px"
                            >
                              {associates.map(a => (
                                <option key={a._id} value={a._id}>
                                  {a.name} ({a.department})({a.position})
                                </option>
                              ))}
                            </Select>
                          </WrapItem>
                        )}
                        <WrapItem>
                          <Button
                            size="sm"
                            colorScheme="purple"
                            onClick={() => setFormData({ ...formData, associates: [...(formData.associates || []), ''] })}
                            isDisabled={
                              formData.associates &&
                              formData.associates.length > 0 &&
                              formData.associates[formData.associates.length - 1] === ''
                            }
                          >
                            + Add Associate
                          </Button>
                        </WrapItem>
                      </Wrap>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Form Actions */}
              <HStack spacing={4} justify="flex-end" pt={4}>
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  size="lg"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="primary"
                  leftIcon={<CheckIcon />}
                  isLoading={isSubmitting}
                  loadingText={isEditMode ? 'Updating Project...' : 'Creating Project...'}
                  size="lg"
                >
                  {isEditMode ? 'Save Changes' : 'Create Project'}
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AddProject;
